import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { formatDateTime, formatNumber, humanize } from '@/utils/format'
import { importQuery, importStatusTone } from './api'

/** Live view of one batch: polls while the worker processes it, then lists rejected rows. */
export function ImportBatchCard({ id }: { id: number }) {
  const { t } = useTranslation('imports')
  const qc = useQueryClient()
  const { data: b } = useQuery(importQuery(id))
  const finished = b?.status === 'completed' || b?.status === 'failed'

  useEffect(() => {
    if (finished) {
      void qc.invalidateQueries({ queryKey: ['imports'], exact: true })
      void qc.invalidateQueries({ queryKey: ['students'] })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    }
  }, [finished, qc])

  if (!b) return null
  const running = !finished

  return (
    <Card>
      <CardHeader
        title={b.fileName}
        description={t('Started by {{name}} · {{date}}', {
          name: b.createdBy,
          date: formatDateTime(b.createdAt),
        })}
        icon={
          running ? (
            <Loader2 className="motion-safe:animate-spin" />
          ) : b.status === 'failed' ? (
            <AlertTriangle />
          ) : (
            <CheckCircle2 />
          )
        }
        actions={
          <Badge tone={importStatusTone[b.status]} dot>
            {b.status === 'processing' ? t('Processing · {{pct}}%', { pct: b.progress }) : humanize(b.status)}
          </Badge>
        }
      />
      <CardBody className="space-y-5">
        <div
          className="h-2 overflow-hidden rounded-full bg-surface-sunken"
          role="progressbar"
          aria-label={t('Import progress')}
          aria-valuenow={b.progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full rounded-full transition-[width] duration-500 ${b.status === 'failed' ? 'bg-danger-500' : 'bg-brand-500'}`}
            style={{ width: `${b.progress}%` }}
          />
        </div>
        <dl className="grid grid-cols-3 gap-4 text-center" aria-live="polite">
          {[
            ['Rows', b.totalRows, 'text-ink'],
            ['Admitted', b.importedRows, 'text-success-700'],
            ['Rejected', b.failedRows, b.failedRows ? 'text-danger-700' : 'text-ink'],
          ].map(([label, value, cls]) => (
            <div key={label} className="rounded-control bg-surface-muted py-3">
              <dt className="text-xs text-muted">{t(label as string)}</dt>
              <dd className={`mt-0.5 text-xl font-semibold tabular ${cls as string}`}>
                {formatNumber(value as number)}
              </dd>
            </div>
          ))}
        </dl>
        {b.failureReason ? (
          <Alert tone="danger" title={t('The file could not be processed')}>
            {b.failureReason}
          </Alert>
        ) : null}
        {b.errors.length ? (
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('Rejected rows')}</h3>
            <p className="mb-3 text-xs text-muted">
              {t('Fix these rows in your spreadsheet and upload a file containing only them.')}
            </p>
            <div className="max-h-80 scrollbar-thin overflow-y-auto rounded-control border border-line">
              <table className="w-full text-sm">
                <caption className="sr-only">{t('Rejected rows')}</caption>
                <thead className="sticky top-0 bg-surface-muted text-left text-2xs tracking-wider text-muted uppercase">
                  <tr>
                    <th scope="col" className="w-20 px-4 py-2">
                      {t('Line')}
                    </th>
                    <th scope="col" className="px-4 py-2">
                      {t('Reason')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {b.errors.map((e) => (
                    <tr key={e.row} className="border-t border-line">
                      <td className="px-4 py-2 font-mono text-xs">{e.row}</td>
                      <td className="px-4 py-2 text-ink-soft">{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  )
}
